import { Banknote, CreditCard, Fuel, Package, Receipt, Timer, TriangleAlert, Vault } from 'lucide-react';

import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn, formatCurrency, formatCurrencyAdaptive, formatNumber } from '@/lib/utils';

import {
  formatFechaHoraTurno,
  formatHoraTurno,
  formatTiempoTranscurrido,
  tonoAntiguedad,
} from '../lib/turnos-format';
import type { MetodoPagoTurno, TurnoEnVivo } from '../turnos.types';

interface TurnoCardProps {
  turno: TurnoEnVivo;
}

interface DatoProps {
  label: string;
  value: string;
  tone?: 'default' | 'positive' | 'warning' | 'danger' | 'muted';
}

const TONO_VALOR: Record<NonNullable<DatoProps['tone']>, string> = {
  default: 'text-ink-900',
  positive: 'text-emerald-600',
  warning: 'text-amber-600',
  danger: 'text-rose-600',
  muted: 'text-ink-400',
};

function Dato({ label, value, tone = 'default' }: DatoProps) {
  return (
    <div className="min-w-0">
      <p className="truncate text-[11px] uppercase tracking-wider text-ink-400">{label}</p>
      <p
        title={value}
        className={cn('truncate font-mono text-sm font-semibold tabular-nums', TONO_VALOR[tone])}
      >
        {value}
      </p>
    </div>
  );
}

function MetodosPago({ metodos }: { metodos: MetodoPagoTurno[] }) {
  if (metodos.length === 0) {
    return <p className="text-xs text-ink-400">Sin pagos registrados todavía.</p>;
  }

  return (
    <ul className="space-y-1.5">
      {metodos.map((metodo) => (
        <li
          key={`${metodo.metodo_pago}-${metodo.tipo}`}
          className="flex items-center justify-between gap-3 rounded-lg border border-ink-100 px-3 py-1.5"
        >
          <span className="flex min-w-0 items-center gap-2">
            {metodo.es_efectivo ? (
              <Banknote className="size-3.5 shrink-0 text-emerald-500" aria-hidden="true" />
            ) : (
              <CreditCard className="size-3.5 shrink-0 text-brand-500" aria-hidden="true" />
            )}
            <span className="truncate text-xs font-medium text-ink-700">{metodo.metodo_pago}</span>
            <Badge variant={metodo.es_efectivo ? 'success' : 'outline'}>
              {metodo.total_transacciones}
            </Badge>
          </span>
          <span className="shrink-0 font-mono text-xs font-semibold text-ink-800 tabular-nums">
            {formatCurrency(metodo.total_monto)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Tarjeta de un turno abierto con su detalle de monitoreo. */
export function TurnoCard({ turno }: TurnoCardProps) {
  const tono = tonoAntiguedad(turno);
  const sinVentas = turno.ventas.transacciones === 0;

  const marco =
    tono === 'danger'
      ? 'border-rose-200'
      : tono === 'warn'
        ? 'border-amber-200'
        : 'border-ink-100';

  return (
    <Card className={cn('overflow-hidden', marco)}>
      {/* Encabezado */}
      <div className="relative flex flex-wrap items-start justify-between gap-3 overflow-hidden bg-gradient-to-r from-brand-600 to-accent-600 px-5 py-4 text-white">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={turno.operador} size="md" />

          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{turno.operador}</p>

            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-brand-50/90">
              <Timer className="size-3.5" aria-hidden="true" />
              Abierto {formatHoraTurno(turno.hora_llegada)} · {formatFechaHoraTurno(turno.hora_llegada).split(' ')[0]}
              <span className="text-white/40">·</span>
              <span className="font-medium text-white">
                {formatTiempoTranscurrido(turno.minutos_transcurridos)}
              </span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="neutral">Turno #{turno.id}</Badge>

          {tono === 'danger' ? (
            <Badge variant="danger" title="Turno abierto hace más de una semana: probable turno sin cerrar">
              <TriangleAlert className="size-3" aria-hidden="true" />
              Sin cerrar hace {turno.antiguedad_dias} días
            </Badge>
          ) : tono === 'warn' ? (
            <Badge variant="warning">
              <TriangleAlert className="size-3" aria-hidden="true" />
              Abierto hace más de 24 h
            </Badge>
          ) : (
            <Badge variant="success">En curso</Badge>
          )}

          {turno.estado_verificacion === 1 ? null : (
            <Badge variant="outline" className="border-white/40 text-white">
              Por verificar
            </Badge>
          )}
        </div>
      </div>

      {/* Cuerpo */}
      <div className="space-y-4 px-5 py-4">
        {/* Lados */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-ink-500">
            <Fuel className="size-3.5 text-ink-400" aria-hidden="true" />
            Lados
          </span>

          {turno.lados.length > 0 ? (
            turno.lados.map((lado) => (
              <Badge key={lado} variant="brand">
                #{lado}
              </Badge>
            ))
          ) : (
            <span className="text-xs text-ink-400">
              Sin lados asignados (el despacho de PTS no se puede medir)
            </span>
          )}
        </div>

        {/* Ventas y despacho */}
        <div className="grid grid-cols-2 gap-4 rounded-control bg-gradient-to-br from-brand-50 to-accent-50 px-4 py-3">
          <Dato
            label="Ventas acumuladas"
            value={formatCurrencyAdaptive(turno.ventas.monto, 100_000)}
            tone={sinVentas ? 'muted' : 'default'}
          />
          <Dato label="Transacciones" value={formatNumber(turno.ventas.transacciones)} />
          <Dato
            label="Ticket promedio"
            value={turno.ventas.transacciones > 0 ? formatCurrency(turno.ventas.ticket_promedio) : '—'}
          />
          <Dato
            label="Despacho PTS"
            value={
              turno.lados.length > 0 ? `${formatNumber(turno.despacho_pts.galones)} gal` : '—'
            }
            tone={turno.lados.length > 0 ? 'default' : 'muted'}
          />
        </div>

        {/* Productos extras (lubricantes) */}
        <div className="flex items-center justify-between gap-3 rounded-control border border-violet-100 bg-violet-50/70 px-3.5 py-2.5">
          <span className="flex items-center gap-2 text-xs font-medium text-ink-600">
            <Package className="size-4 text-violet-500" aria-hidden="true" />
            Productos extras
          </span>
          <span
            title={formatCurrency(turno.lubricantes?.monto ?? 0)}
            className="font-mono text-sm font-semibold text-ink-900 tabular-nums"
          >
            {formatCurrencyAdaptive(turno.lubricantes?.monto ?? 0, 100_000)}
          </span>
        </div>

        {/* Cobros, gastos y bóvedas */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-start gap-2">
            <Banknote className="mt-0.5 size-4 shrink-0 text-emerald-500" aria-hidden="true" />
            <div className="min-w-0">
              <Dato
                label="Cobrado en efectivo"
                value={formatCurrencyAdaptive(turno.cobrado_efectivo, 100_000)}
                tone="positive"
              />
            </div>
          </div>

          <div className="flex items-start gap-2">
            <CreditCard className="mt-0.5 size-4 shrink-0 text-brand-500" aria-hidden="true" />
            <div className="min-w-0">
              <Dato label="Otros medios" value={formatCurrencyAdaptive(turno.cobrado_no_efectivo, 100_000)} />
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Receipt className="mt-0.5 size-4 shrink-0 text-amber-500" aria-hidden="true" />
            <div className="min-w-0">
              <Dato
                label={`Gastos (${turno.gastos.cantidad})`}
                value={formatCurrencyAdaptive(turno.gastos.total, 100_000)}
                tone={turno.gastos.total > 0 ? 'warning' : 'muted'}
              />
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Vault className="mt-0.5 size-4 shrink-0 text-ink-400" aria-hidden="true" />
            <div className="min-w-0">
              <Dato
                label={`Bóvedas (${turno.bovedas.cantidad})`}
                value={formatCurrencyAdaptive(turno.bovedas.total, 100_000)}
                tone={turno.bovedas.total > 0 ? 'default' : 'muted'}
              />
            </div>
          </div>
        </div>

        {/* Métodos de pago */}
        <div className="border-t border-ink-100 pt-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            Métodos de pago del turno
          </p>
          <MetodosPago metodos={turno.metodos_pago} />
        </div>

        {turno.adelantos.cantidad > 0 ? (
          <p className="text-xs text-ink-500">
            {turno.adelantos.cantidad}{' '}
            {turno.adelantos.cantidad === 1 ? 'pago adelantado' : 'pagos adelantados'} en este turno
            (se cobraron antes de la apertura).
          </p>
        ) : null}
      </div>
    </Card>
  );
}
